import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { AlertController, IonicModule, ModalController } from '@ionic/angular';
import { EventOfficial } from '../../../models/event-official.model';
import { Match } from '../../../models/match.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OfficialService } from '../official.service';
import { EmptyPageModule } from '../../../../empty-page/empty-page.module';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { from, map, mergeMap, Observable, Subject, takeUntil } from 'rxjs';
import { Court } from '../../../models/csTypes/court';
import { Tournament } from '../../../models/tournament.model';
import { OfficialsComponent } from '../officials.component';
import { Venue } from '../../../models/csTypes/venue';
import { TournamentDetailsService } from '../../../../tournaments/tournament-details/tournament-details.service';
import { ToastService } from '../../../../_core/services/toast.service';
import { Camera } from '../../../models/camera.model';
import { VideoStreamProviderService } from '../../../../video-stream-provider-list/video-stream-provider.service';
import { VenueVideoBoardsService } from '../../../../venues/venue-details/venue-video-boards/venue-video-boards.service';
import { MatchAssignments, MatchService } from '../../../../_core/services/match.service';

interface OfficialAssignment {
  venue: FormControl<Venue | null>;
  court: FormControl<Court | null>
}

@Component({
  selector: 'app-assigned-official',
  templateUrl: './assigned-official.component.html',
  styleUrls: ['./assigned-official.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    TranslateModule,
    EmptyPageModule,
    ReactiveFormsModule
  ]
})
export class AssignedOfficialComponent implements OnInit, OnDestroy {
  private modalController = inject(ModalController);
  private officialService = inject(OfficialService);
  private matchService = inject(MatchService);
  private tournamentDetailsService = inject(TournamentDetailsService);
  private toastService = inject(ToastService);
  private alertController = inject(AlertController);
  private videoStreamProviderService = inject(VideoStreamProviderService);
  private venueVideoBoardsService = inject(VenueVideoBoardsService);
  private translateService = inject(TranslateService);
  @Input({ required: true}) _matchId: string;
  @Input({ required: true}) _tournamentId: string;
  @Input({ required: true}) match: Match;

  isAssignModalOpen = false;

  official: EventOfficial;

  assignOfficialForm = new FormGroup<OfficialAssignment>({
    venue: new FormControl(null),
    court: new FormControl(null)
  })

  courts$: Observable<Court[]> = this.assignOfficialForm
    .controls
    .venue
    .valueChanges
    .pipe(
      map(venue => this.tournament?.venues?.find(v => v.venueId === venue?.venueId)?.courts || [])
    )

  tournament: Tournament;
  devicesToAssign: Camera[] = [];
  videoBoardsToAssign: string[] = [];

  private readonly destroy$: Subject<void> = new Subject();

  async ngOnInit() {
    this.tournament = await this.tournamentDetailsService.getTournamentInfo(this._tournamentId);
    this.assignOfficialForm
      .controls
      .court
      .valueChanges
      .subscribe(res => {
        this.devicesToAssign = [];
        this.videoBoardsToAssign = [];
      })
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  submit() {
    if (this.assignOfficialForm.invalid) {
      this.toastService.presentDangerToast(this.translateService.instant('Please select an official'));
      return;
    }

    const matchAssignments: MatchAssignments = {
      _officialsUserId: this.official?.user?._id,
      _tournamentId: this._tournamentId,
      venue: this.assignOfficialForm.controls.venue.value as Venue,
      court: this.assignOfficialForm.controls.court.value as Court,
      _videoBoardIds: this.videoBoardsToAssign
    }

    this.matchService
      .matchAssignments(this._matchId, matchAssignments)
      .subscribe(
        async (match: Match) => {
          this.match.officials = match.officials;
          this.match.videoBoards = match.videoBoards;
          this.isAssignModalOpen = false;

          if (this.devicesToAssign.length) {
            this.saveCamera()
          }
        }
      )
  }

  async saveCamera(removeOldCameras?: boolean, keepVideoLink?: boolean) {
    from(this.devicesToAssign).pipe(
      takeUntil(this.destroy$),
      mergeMap(camera =>  this.videoStreamProviderService.saveCameraToMatch(
        camera, 
        this.match._id, 
        removeOldCameras || false, 
        false,
        keepVideoLink || false
      ))
    )
    .subscribe(
      async res => {
        if (res.data.matchWithCamera) {
          const alert = await this.alertController.create({
            header: this.translateService.instant('Device is in use'),
            message: this.translateService.instant('This device is currently assigned to another match. Do you want to save the video recording from that match?'),
            buttons: [
              {
                text: this.translateService.instant('Don\'t save and remove'),
                role: 'destructive',
                handler: () => {
                  this.saveCamera(true, false);
                }
              },
              {
                text: this.translateService.instant('Save and remove'),
                handler: () => {
                  this.saveCamera(true, true);
                }
              },
              {
                text: this.translateService.instant('Cancel'),
                role: 'cancel'
              }
            ]
          });

          await alert.present();
        } else {
          this.match.cameras = res.data.cameras;
          if (res.data.videoLinks) {
            this.match.videoLinks = res.data.videoLinks;
          }
          this.toastService.presentToast(this.translateService.instant('Device assigned successfully'));
        }
      }
    );
}

  removeAssignedOfficial(official: EventOfficial, index: number) {
    this.officialService
      .removeOfficialFromMatch(this._matchId, official._id, official?.user?._id || '', this._tournamentId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(
        res => {
          this.match.officials.splice(index, 1);
        }
      )
  }

  selectDevices(ev, camera: Camera) {
    if (ev.detail.checked) {
      this.devicesToAssign.push(camera)
    } else {
      const index = this.devicesToAssign.findIndex(d => d.token === camera.token);
      if (index > -1) {
        this.devicesToAssign.splice(index, 1);
      }
    }
  }

  selectVideoBoards(ev, _videoBoardId: string) {
    if (ev.detail.checked) {
      this.videoBoardsToAssign.push(_videoBoardId)
    } else {
      const index = this.videoBoardsToAssign.findIndex(videoBoardId => videoBoardId === _videoBoardId);
      if (index > -1) {
        this.videoBoardsToAssign.splice(index, 1);
      }
    }
  }

  getOfficialName(official: EventOfficial) {
    return `${official.user?.last_name?.toUpperCase()}, ${official.user?.first_name }`
  }

  async getOfficial() {
    const modal = await this.modalController.create({
      component: OfficialsComponent,
      componentProps: {
        _tournamentId: this._tournamentId,
        isEdit: false,
        available: 'AVAILABLE'
      }
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();

    this.official = data;
  }

  async close() {
    await this.modalController.dismiss();
  }

}
